import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { getACPRecipientList } from '@api/resident';
import { SharingACPDetails } from '@typings/ACPTypes';
import { ApiResponse } from '@typings/ApiResponse';
import Pagination from '../../../pagination';
import { SharingACPTypes } from './utils';

interface SharedCarePlansTableProps {
  userId: string;
  formSubmitted: boolean;
}

interface RecipientListResponse {
  count: number;
  pages: number;
  data: SharingACPDetails[];
}

const SharedPrintedCopiesTable: React.FC<SharedCarePlansTableProps> = ({
  userId,
  formSubmitted,
}) => {
  const [recipientList, setRecipientList] = useState<SharingACPDetails[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const getACPList = async () => {
      setLoading(true);
      try {
        const response: ApiResponse<RecipientListResponse> =
          await getACPRecipientList(
            userId,
            currentPage,
            5,
            SharingACPTypes.PRINT
          );
        if (response && response.status === 'success') {
          const tranformedData = response.data.data?.map(
            (recipient: SharingACPDetails) => {
              return {
                ...recipient,
                dateShared: recipient.created_at,
                key: recipient.id,
              };
            }
          );
          setRecipientList(tranformedData);
          setTotalPages(response.data.pages);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    getACPList();
  }, [userId, currentPage, formSubmitted]);

  const getNextOfKinInfo = (nextOfKin: any) => {
    if (nextOfKin === null || nextOfKin === undefined) {
      return { text: '-', className: '' };
    }
    if (nextOfKin === true) {
      return { text: 'Yes', className: 'govuk-tag--green' };
    }
    return { text: 'No', className: 'govuk-tag--red' };
  };

  return (
    <div className="mt-10 govuk-bp:mt-14">
      <div className="overflow-x-auto">
        <table className="govuk-table ">
          <thead className="govuk-table__head">
            <tr className="govuk-table__row">
              <th
                scope="col"
                className="govuk-table__header govuk-!-width-one-quarter"
              >
                Full name
              </th>
              <th
                scope="col"
                className="govuk-table__header govuk-!-width-one-quarter"
              >
                Relationship
              </th>
              <th
                scope="col"
                className="govuk-table__header govuk-!-width-one-quarter"
              >
                Email address
              </th>
              <th
                scope="col"
                className="govuk-table__header govuk-!-width-one-quarter govuk-!-text-align-centre"
              >
                Next of Kin
              </th>
              <th
                scope="col"
                className="govuk-table__header govuk-!-width-one-quarter"
              >
                Date Shared
              </th>
            </tr>
          </thead>
          <tbody className="govuk-table__body">
            {recipientList.map(recipient => {
              return (
                <tr className="govuk-table__row" key={recipient.id}>
                  <td className="govuk-table__cell govuk-!-width-one-quarter">
                    {recipient.name}
                  </td>
                  <td className="govuk-table__cell govuk-!-width-one-quarter govuk-!-padding-right-2">
                    {recipient.relationship}
                  </td>
                  <td className="govuk-table__cell govuk-!-width-one-quarter govuk-!-padding-right-2">
                    {recipient.email}
                  </td>
                  <td className="govuk-table__cell govuk-!-width-one-quarter govuk-!-padding-right-2 govuk-!-text-align-centre">
                    {(() => {
                      const { text, className } = getNextOfKinInfo(
                        recipient.next_of_kin
                      );
                      return (
                        <strong className={`govuk-tag ${className}`}>
                          {text}
                        </strong>
                      );
                    })()}
                  </td>
                  <td className="govuk-table__cell govuk-!-width-one-quarter govuk-!-padding-right-2">
                    {dayjs(recipient.created_at).format('DD/MM/YYYY')}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex justify-end">
        <Pagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={totalPages}
        />
      </div>
    </div>
  );
};

export default SharedPrintedCopiesTable;
